import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, InputAdornment,
  Chip, IconButton, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Card, CardContent, Avatar, Tabs, Tab,
  List, ListItem, ListItemText, Divider, CircularProgress, Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ShoppingBag as OrderIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  PersonAdd as NewMemberIcon
} from '@mui/icons-material';
import api from '../../services/api';

const AdminMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [memberOrders, setMemberOrders] = useState([]);
  const [detailTab, setDetailTab] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    newThisMonth: 0,
    activeThisMonth: 0
  });

  // 載入會員列表
  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/members', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined
        }
      });
      setMembers(response.data.members || []);
      setTotalCount(response.data.total || 0);
      setStats(response.data.stats || stats);
      setError(null);
    } catch (err) {
      console.error('載入會員失敗:', err);
      setError('載入會員資料失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [page, rowsPerPage, search]);

  // 查看會員詳情
  const handleViewMember = async (member) => {
    setSelectedMember(member);
    setDetailOpen(true);
    setDetailTab(0);

    // 載入會員訂單
    try {
      const response = await api.get(`/admin/members/${member.id}/orders`);
      setMemberOrders(response.data.orders || []);
    } catch (err) {
      console.error('載入會員訂單失敗:', err);
      setMemberOrders([]);
    }
  };

  // 切換會員狀態
  const handleToggleStatus = async (memberId, currentStatus) => {
    try {
      await api.put(`/admin/members/${memberId}/status`, {
        status: currentStatus === 'active' ? 'blocked' : 'active'
      });
      fetchMembers();
    } catch (err) {
      console.error('更新狀態失敗:', err);
    }
  };

  // 格式化日期
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // 統計卡片
  const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {value.toLocaleString()}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main` }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        會員管理
      </Typography>

      {/* 統計卡片 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="總會員數"
            value={stats.total}
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="本月新會員"
            value={stats.newThisMonth}
            icon={<NewMemberIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="本月活躍會員"
            value={stats.activeThisMonth}
            icon={<TrendingUpIcon />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* 搜尋區 */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="搜尋會員 (姓名、電話、Email)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          size="small"
        />
      </Paper>

      {/* 錯誤提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 會員列表 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>會員</TableCell>
                <TableCell>聯絡方式</TableCell>
                <TableCell align="center">訂單數</TableCell>
                <TableCell align="right">消費總額</TableCell>
                <TableCell align="center">狀態</TableCell>
                <TableCell>註冊日期</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      {search ? '找不到符合的會員' : '尚無會員資料'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={member.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                          {member.name?.charAt(0) || <PersonIcon />}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {member.name || '未設定'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {member.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {member.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">{member.phone}</Typography>
                          </Box>
                        )}
                        {member.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {member.email}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={member.order_count || 0}
                        size="small"
                        color={member.order_count > 0 ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="medium" color="success.main">
                        ${(member.total_spent || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={member.status === 'blocked' ? '停用' : '正常'}
                        size="small"
                        color={member.status === 'blocked' ? 'error' : 'success'}
                        icon={member.status === 'blocked' ? <BlockIcon /> : <ActiveIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(member.created_at)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleViewMember(member)}
                        title="查看詳情"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(member.id, member.status)}
                        title={member.status === 'blocked' ? '啟用' : '停用'}
                        color={member.status === 'blocked' ? 'success' : 'error'}
                      >
                        {member.status === 'blocked' ? <ActiveIcon /> : <BlockIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="每頁顯示"
        />
      </Paper>

      {/* 會員詳情 Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              {selectedMember?.name?.charAt(0) || <PersonIcon />}
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedMember?.name || '未設定姓名'}</Typography>
              <Typography variant="body2" color="text.secondary">
                會員 ID: {selectedMember?.id}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={detailTab}
            onChange={(e, v) => setDetailTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="基本資料" />
            <Tab label="訂單記錄" />
          </Tabs>

          {detailTab === 0 && selectedMember && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    聯絡資訊
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="電話"
                        secondary={selectedMember.phone || '-'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Email"
                        secondary={selectedMember.email || '-'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="LINE ID"
                        secondary={selectedMember.line_id || '-'}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    消費統計
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="訂單數量"
                        secondary={`${selectedMember.order_count || 0} 筆`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="消費總額"
                        secondary={`$${(selectedMember.total_spent || 0).toLocaleString()}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="最後消費"
                        secondary={formatDate(selectedMember.last_order_date)}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    帳號資訊
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="註冊日期"
                        secondary={formatDate(selectedMember.created_at)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="帳號狀態"
                        secondary={
                          <Chip
                            label={selectedMember.status === 'blocked' ? '已停用' : '正常'}
                            size="small"
                            color={selectedMember.status === 'blocked' ? 'error' : 'success'}
                          />
                        }
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>
          )}

          {detailTab === 1 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>訂單編號</TableCell>
                    <TableCell>日期</TableCell>
                    <TableCell align="right">金額</TableCell>
                    <TableCell align="center">狀態</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {memberOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">尚無訂單記錄</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    memberOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>{order.order_no}</TableCell>
                        <TableCell>{formatDate(order.created_at)}</TableCell>
                        <TableCell align="right">${order.total_amount?.toLocaleString()}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={order.status}
                            size="small"
                            color={
                              order.status === 'completed' ? 'success' :
                              order.status === 'cancelled' ? 'error' : 'warning'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>關閉</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMembers;
